import React, { useState, useEffect, useRef } from "react";
import { api } from "../../lib/apiClient";
import { Download, Edit3, Plus, Save, Trash2, Upload } from "lucide-react";
import { toast } from "../../components/ui/sonner";
import { useConfirm } from "../../components/ConfirmDialog";
import { Modal } from "../../components/Modal";

export function ProductsTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const fileRef = useRef();
  const [importInfo, setImportInfo] = useState("");
  const confirm = useConfirm();

  const load = () => api.get("/products").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const newProduct = () => setEditing({
    name: "", category: "Parts",
    install_hours_fresh: 0, install_hours_ripout: 0,
  });

  const save = async () => {
    const body = { ...editing };
    if (editing.id) await api.put(`/products/${editing.id}`, body);
    else await api.post("/products", body);
    setEditing(null);
    load();
  };

  const remove = async (id) => {
    const ok = await confirm({
      title: "Delete this product?",
      description: "It will be removed from the catalogue. Existing quotes are unaffected.",
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    await api.delete(`/products/${id}`);
    load();
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/products/import", fd, { headers: { "Content-Type": "multipart/form-data" }});
      const skip = data.skipped_duplicates ? `, ${data.skipped_duplicates} duplicate${data.skipped_duplicates === 1 ? "" : "s"} skipped` : "";
      setImportInfo(`Imported · ${data.inserted} new${skip}${data.errors?.length ? `, ${data.errors.length} errors` : ""}`);
    } catch (err) {
      setImportInfo(err?.response?.data?.detail || "Import failed");
    }
    e.target.value = "";
    load();
  };

  const downloadTemplate = () => {
    const csv = "name,category,install_hours_fresh,install_hours_ripout,complex_hours\nExample Motor,Parts,0,0,0\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "products-template.csv";
    a.click();
  };

  const downloadList = async () => {
    try {
      const res = await api.get("/products/export", { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = "products.csv";
      a.click();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not export");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={newProduct} data-testid="new-product-button" className="h-9 px-4 bg-brand-blue text-white text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2 hover:bg-brand-blue-dark">
          <Plus size={14} /> Add Product
        </button>
        <button onClick={() => fileRef.current?.click()} className="h-9 px-4 border border-slate-900 hover:bg-slate-50 text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2" data-testid="import-csv-button">
          <Upload size={14} /> Import CSV
        </button>
        <button onClick={downloadList} className="h-9 px-4 border border-slate-300 hover:border-slate-900 text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2" data-testid="products-export-button">
          <Download size={14} /> Export CSV
        </button>
        <button onClick={downloadTemplate} className="h-9 px-4 border border-slate-300 hover:border-slate-900 text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2">
          <Download size={14} /> Template
        </button>
        <input ref={fileRef} type="file" accept=".csv" onChange={upload} className="hidden" />
        {importInfo && <span className="text-xs text-slate-500 ml-2">{importInfo}</span>}
      </div>

      <div className="border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b-2 border-slate-900">
              {["Name", "Category", "Hrs Fresh", "Hrs Rip-out", "Complex hrs", ""].map((h) => (
                <th key={h} className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-600">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-b border-slate-200 hover:bg-slate-50">
                <td className="px-3 py-2 font-medium">{p.name}</td>
                <td className="px-3 py-2 text-slate-600">{p.category}</td>
                <td className="px-3 py-2 font-mono">{p.install_hours_fresh}</td>
                <td className="px-3 py-2 font-mono">{p.install_hours_ripout}</td>
                <td className="px-3 py-2 font-mono text-slate-500">{p.complex_hours ?? "—"}</td>
                <td className="px-3 py-2 text-right space-x-2">
                  <button onClick={() => setEditing(p)} className="text-slate-500 hover:text-slate-900"><Edit3 size={14} /></button>
                  <button onClick={() => remove(p.id)} className="text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <ProductDialog product={editing} setProduct={setEditing} save={save} />
      )}
    </div>
  );
}

function ProductDialog({ product, setProduct, save }) {
  const set = (k, v) => setProduct((p) => ({ ...p, [k]: v }));
  return (
    <Modal
      open={!!product}
      onClose={() => setProduct(null)}
      title={product?.id ? "Edit Product" : "New Product"}
      size="lg"
      footer={
        <>
          <button onClick={() => setProduct(null)} className="h-9 px-4 rounded-full border border-slate-300 text-xs uppercase tracking-wider font-semibold">Cancel</button>
          <button onClick={save} data-testid="save-product-button" className="h-9 px-4 rounded-full bg-brand-blue text-white text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2 hover:bg-brand-blue-dark">
            <Save size={14} /> Save
          </button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-4 text-sm">
        {[
          ["Name", "name", "text", true],
          ["Category", "category", "select"],
          ["Install Hours Fresh", "install_hours_fresh", "number"],
          ["Install Hours Rip-out", "install_hours_ripout", "number"],
          ["Complex hours (leave blank = use global default)", "complex_hours", "number"],
        ].map(([label, k, type, req]) => (
          <div key={k} className={k === "complex_hours" ? "col-span-2" : ""}>
            <label className="text-[10px] uppercase tracking-wider text-slate-600 mb-1 block">{label}{req && " *"}</label>
            {type === "select" ? (
              <select value={product[k] || ""} onChange={(e) => set(k, e.target.value)} className="h-9 w-full border border-slate-300 px-2">
                {["Parts", "Door", "Powder Coating", "Transport", "Plant Hire", "Other"].map((c) => <option key={c}>{c}</option>)}
              </select>
            ) : (
              <input
                type={type}
                value={product[k] ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  if (type === "number") {
                    set(k, v === "" ? null : Number(v));
                  } else {
                    set(k, v);
                  }
                }}
                className="h-9 w-full border border-slate-300 px-2 font-mono"
              />
            )}
          </div>
        ))}
      </div>
    </Modal>
  );
}
